// Kept in step with triangula-commander/src/models/scouts.ts; the state derivation lives in ../scoutState.ts.

export enum ScoutState {
  Offline = 1, // Not heard from since a tut - ideally, no connections
  Initializing = 3, // Ge tting tut and position
  Ready = 4, // Ready to record or sync
  Listening = 6,
  InaccurateLocation = 7,
  OfflineIgnore = 8, // Not heard from in such a long time so it should be ignored
  DownloadingDetector = 9, // Downloading the models used for detection, retries included
  DetectionUnavailable = 10, // A detection model could not be downloaded; the scout needs a restart
  Disabled = 11, // Disabled because of bad audio quality
  Hibernate = 12, // Scout is in hibernation mode
  PreparingDetection = 13, // Models on the device but not loaded yet (decrypting, or waiting for a key)
}

export enum TimeState {
  // noinspection JSUnusedGlobalSymbols
  Unknown = 0,
  Initializing = 1,
  Ready = 2,
}

export interface ScoutLocation {
  accuracy: number;
  altitude: number;
  latitude: number;
  longitude: number;
}

export interface DeviceInfo {
  manufacturer: string;
  model: string;
  sdk: string;
}

export interface SampleAverage {
  sampleAverage: number;
  sampleCount: number;
}

export interface AudioStatistics {
  sampleAverages: SampleAverage[];
}

export interface ScoutDisabled {
  metric: string;
  value: string;
}

export interface Detection {
  range?: number;
  disabled?: ScoutDisabled;
}

export interface ScoutConfig {
  hidden?: boolean;
  markerLetter?: string;
}

/**
 * Host health of a JVM scout (Raspberry Pi), reported in every status; Android devices send none. The device
 * decides the warnings, from a fixed vocabulary; the UI only displays them and never re-derives them.
 */
export interface HostStatus {
  /** True when warnings holds "physical" or "heap". */
  lowMemory: boolean;
  /** physical, heap, undervoltage, throttled, hot, overloaded; empty when healthy. */
  warnings: string[];
  /** SoC temperature in °C; the firmware throttles from 80. */
  temperature: number;
  /** The numbers below come only at the Verbose details level; null otherwise. */
  memAvailableMb?: number | null;
  rssMb?: number | null;
  gcPausePercent?: number | null;
  load?: number | null;
  cores?: number | null;
}

/** The Wallie (or other Titan hardware) attached to a JVM scout, as the scout reports it in every status. */
export interface TitanStatus {
  /** Firmware identifier the board runs. */
  firmware: string;
  /** Hardware type, for example "Wallie". */
  type: string;
  /** Seconds since the board last reported to the scout. */
  lastUpdateSec: number;
  /** Minutes since the board booted. */
  uptimeMin: number;
  serialNumber?: string | null;
  version?: string | null;
  /** Hash of the configuration the board runs. */
  configHash?: string | null;
}

export interface ScoutStatus {
  host?: HostStatus | null;
  titan?: TitanStatus | null;
  /** Free-text diagnostics from the scout; JVM scouts embed the Wallie board's statistics in it as JSON. */
  timeService?: { details?: string } | null;
  /** Audio around detections is being uploaded from the scout. */
  upload?: boolean;
  altitude: number;
  appVersion: string;
  batteryPercent: number;
  clockAccuracy: number;
  created: string;
  dateTime: number;
  gain: number;
  timeServiceName?: string;
  deviceInfo: DeviceInfo;
  deviceId: string;
  id: string;
  latitude: number;
  longitude: number;
  listening: boolean;
  referenceMode: boolean;
  searchMode: boolean;
  threshold: number;
  predictionThreshold: number;
  model: string;
  timeOffset: number;
  timeState: number;
  displayName: string;
  usbCharge?: boolean;
  acCharge?: boolean;
  receivedTut?: number;
  scoutLocation?: ScoutLocation;
  audioStatistics?: AudioStatistics;
  moving?: boolean;
  fixedLocation?: boolean;
  detectorId?: number;
  detector?: Detection;
  /** Downloading the detection models, retries included. */
  downloading?: boolean;
  modelsAvailable?: boolean;
  /** A required detection model has used up its download attempts; the scout needs a restart. Absent on older apps. */
  modelsFailed?: boolean;
  hibernate?: boolean;
}

export interface Scout {
  deviceId: string;
  state: ScoutState;
  status: ScoutStatus;
  config?: ScoutConfig;
  gain?: number;
  timeServiceName?: string;
  predictionThreshold?: number;
  timingThreshold?: number;
  model?: string;
  detectorId?: number;
}
